import { type FormEvent, type KeyboardEvent, useId, useState } from "react";
import type { Item } from "./api.ts";
import { itemKind } from "./format.ts";
import { useJustDone } from "./justDone.ts";
import { linkIn } from "./linkIn.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button } from "./souvenir.ts";
import { KindIcon } from "./ui.tsx";

// Changing links on the web (DESIGN.md, Views, the web's own edits; features/links.md, "Add, edit and remove every
// link on the web"): what a card's links (CardLinks.tsx) and Ideas' inbox (LinkCard.tsx) share. Anyone in the trip,
// on a trip that can change; each change one write signed "on the web", said at once with its Undo (the trip's
// notice) and undoable from Updates; each needs signal.

// Running one change: busy while it's sent, its refusal said where it was asked, and once it's written, the trip
// loaded again and the change said with Undo. True when it went through.
export function useLinkChange(trip: string | undefined, onChanged: (() => void) | undefined) {
  const say = useJustDone();
  const readOnly = useReadOnly();
  const online = useOnline();
  const kept = useKept();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (send: () => Promise<{ change: string | null }>, text: string): Promise<boolean> => {
    if (!trip || busy) return false;
    setError(null);
    setBusy(true);
    try {
      const { change } = await send();
      onChanged?.();
      if (change) say({ change, text });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't change the link. Try again.");
      // Refused as someone changed it meanwhile: what's shown is loaded again, so a second try is over it.
      onChanged?.();
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { can: trip !== undefined && !readOnly, busy, offline: !online || kept !== null, error, run, setError };
}

// What a change's refusal says, under where it was asked.
export function LinkError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="caption tm-error" role="alert">
      {error}
      <ReportThis />
    </p>
  );
}

// "Gogyo Ramen", "Gogyo Ramen and 2 more": what a link is about, by the first's title.
export function aboutWords(ids: string[], items: Item[]): string {
  const titles = ids.flatMap((id) => items.find((i) => i.id === id)?.title ?? []);
  const [first] = titles;
  if (!first) return "";
  return `${first}${titles.length > 1 ? ` and ${titles.length - 1} more` : ""}`;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NOTE = 1000;

export interface LinkValues {
  url: string;
  note: string | null;
  items: string[];
}

// A link's address to paste or change, with its note and what it's about where those are asked (`note`, `about`):
// Add a link on a card, and Edit wherever a link is. A web page is taken as SaveLink takes one (linkIn.ts: words with
// a link in them give the link, "expedia.com/x" is https://expedia.com/x); an email address (`email`) as one. Escape
// with nothing changed puts it away, as Cancel does; with something typed, it stays (and the card under it with it).
export function LinkForm({
  label,
  ariaLabel,
  email = false,
  start = { url: "", note: null, items: [] },
  note = false,
  about,
  saveLabel = "Save",
  busy,
  offline,
  error,
  onSave,
  onCancel,
}: {
  // The address field's label: "Add a link", "Address".
  label: string;
  // The form's own name, for those who can't see it: "Edit the link en.ichiran.com".
  ariaLabel: string;
  email?: boolean;
  start?: LinkValues;
  note?: boolean;
  // The trip's items, to pick what it's about from (AboutPicker); none, not asked.
  about?: Item[];
  saveLabel?: string;
  busy: boolean;
  offline: boolean;
  error: string | null;
  onSave: (values: LinkValues) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [url, setUrl] = useState(start.url);
  const [words, setWords] = useState(start.note ?? "");
  const [items, setItems] = useState(start.items);
  const [problem, setProblem] = useState<string | null>(null);
  const changed =
    url.trim() !== start.url ||
    (words.trim() || null) !== (start.note ?? null) ||
    JSON.stringify(items) !== JSON.stringify(start.items);
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (busy || offline || !changed) return;
    const bare = url.trim().replace(/^mailto:/i, "");
    const address = email ? (EMAIL.test(bare) ? bare : null) : linkIn(url);
    if (!address) {
      setProblem(
        email
          ? "An email address, like info@ichiran.com"
          : "Paste a link, like https://www.tiktok.com/@kyotofoodie/video/…",
      );
      return;
    }
    setProblem(null);
    onSave({ url: address, note: words.trim() || null, items });
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    if (!changed) onCancel();
  };
  return (
    <form className="tm-link-form" aria-label={ariaLabel} noValidate onSubmit={save} onKeyDown={onKeyDown}>
      <div className={`sv-field tm-booking-field${problem ? " is-wrong" : ""}`}>
        <label className="sv-field-label" htmlFor={`${id}-url`}>
          {label}
        </label>
        <input
          id={`${id}-url`}
          className="sv-field-input"
          type={email ? "email" : "url"}
          inputMode={email ? "email" : "url"}
          autoComplete="off"
          // biome-ignore lint/a11y/noAutofocus: opened by its own button, the field it asked for
          autoFocus
          placeholder={email ? "info@ichiran.com" : "Paste a link: a video, a post, a blog"}
          value={url}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => {
            setUrl(e.target.value);
            setProblem(null);
          }}
        />
        {problem && <span className="sv-field-hint tm-error">{problem}</span>}
      </div>
      {note && (
        <div className="sv-field tm-booking-field">
          <label className="sv-field-label" htmlFor={`${id}-note`}>
            A note, if you like
          </label>
          <input
            id={`${id}-note`}
            className="sv-field-input"
            autoComplete="off"
            maxLength={MAX_NOTE}
            placeholder="What it is, who found it"
            value={words}
            onChange={(e) => setWords(e.target.value)}
          />
        </div>
      )}
      {about && <AboutPicker items={about} chosen={items} onChange={setItems} />}
      {offline && <p className="caption tm-muted tm-link-form-note">No signal: changing it needs signal.</p>}
      <LinkError error={error} />
      <span className="tm-note-actions">
        <Button variant="secondary" size="sm" type="submit" disabled={busy || offline || !changed || !url.trim()}>
          {busy ? "Saving…" : saveLabel}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

// What a link can be about: a place, a goal or a stay, or one of a choice's options; nothing dropped from the trip
// (DESIGN.md, the log row "Links on the web": "the places, goals or options it's about").
export function aboutCandidates(items: Item[]): Item[] {
  return items.filter(
    (i) =>
      i.status !== "dropped" &&
      (i.type === "place" ||
        i.type === "goal" ||
        i.type === "stay" ||
        i.status === "option" ||
        i.status === "not_chosen"),
  );
}

// Folded for finding: no case, no accents ("Kinkaku-ji" for "kinkaku", "Café" for "cafe").
const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

// Which places, goals or options a link is about, picked from the trip: those it's about first, ticked, then what
// matches the search (by name, its area or its city), each ticked to add it or unticked to take it off.
export function AboutPicker({
  items,
  chosen,
  onChange,
}: {
  items: Item[];
  chosen: string[];
  onChange: (ids: string[]) => void;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  // Those it's about as they were when the picker opened stay in place as they're unticked, so a row never jumps
  // from under a finger.
  const [first] = useState(chosen);
  const candidates = aboutCandidates(items);
  const byId = new Map(items.map((i) => [i.id, i]));
  const pinned = [...new Set([...first, ...chosen])].flatMap((c) => byId.get(c) ?? []);
  const q = fold(query.trim());
  const matches = candidates
    .filter((i) => !pinned.includes(i))
    .filter((i) => !q || fold([i.title, i.where?.name, i.where?.city].filter(Boolean).join(" ")).includes(q))
    .sort((a, b) => a.title.localeCompare(b.title));
  const toggle = (item: string, on: boolean) => onChange(on ? [...chosen, item] : chosen.filter((c) => c !== item));
  return (
    <fieldset className="tm-about">
      <legend className="sv-field-label tm-about-label">What it's about</legend>
      <input
        id={`${id}-find`}
        className="sv-field-input tm-about-find"
        type="search"
        autoComplete="off"
        aria-label="Find a place, goal or option"
        placeholder="Find a place, goal or option"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {pinned.length + matches.length > 0 ? (
        <ul className="tm-about-list">
          {[...pinned, ...matches].map((i) => (
            <li key={i.id}>
              <label className="tm-about-option">
                <input
                  type="checkbox"
                  checked={chosen.includes(i.id)}
                  onChange={(e) => toggle(i.id, e.target.checked)}
                />
                <KindIcon item={i} size={16} className="tm-row-icon tm-about-icon" />
                <span className="tm-about-words">
                  <span className="callout tm-about-title">{i.title}</span>
                  <span className="caption tm-muted tm-about-kind">{itemKind(i)}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <p className="caption tm-muted tm-about-none">
          {candidates.length === 0
            ? "No places, goals or options in the trip yet."
            : `Nothing in the trip called “${query.trim()}”.`}
        </p>
      )}
    </fieldset>
  );
}
